import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Button, 
  Avatar, 
  Chip, 
  CircularProgress, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Paper,
  Snackbar
} from '@mui/material';
import { 
  Add as AddIcon, 
  Remove as RemoveIcon, 
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Group as TeamIcon,
  Assignment as DraftIcon
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { usePlayer } from '../contexts/PlayerContext';
import { usePageContent } from '../contexts/PageContentContext';
import { playerService, Player, User, FantasyTeam, FantasyTeamPlayer, FantasyLeague } from '../services/playerService';
import { useTheme } from '@mui/material/styles';

const HomePage: React.FC = () => {
  const theme = useTheme();
  const { currentUsername, isUserSwitching } = useUser();
  const { openPlayerDialog } = usePlayer();
  const { setPageState } = usePageContent();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userFantasyTeams, setUserFantasyTeams] = useState<FantasyTeam[]>([]);
  const [userFantasyLeagues, setUserFantasyLeagues] = useState<FantasyLeague[]>([]);
  const [userPlayers, setUserPlayers] = useState<FantasyTeamPlayer[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]); // Added for all players data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Extract the main content rendering to a separate function
  const renderHomeContent = () => {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Welcome back, {currentUser?.firstName}! 👋
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Down Under Dynasty - Your NBL Fantasy Experience
          </Typography>
        </Box>

        {/* League Status Card */}
        {primaryLeague && (
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                🏆 League Status
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography color="text.secondary">League Name:</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography sx={{ fontWeight: 'bold' }}>{primaryLeague.name}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography color="text.secondary">Status:</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography sx={{ fontWeight: 'bold', color: 'primary.main' }}>{primaryLeague.status}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography color="text.secondary">Teams:</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography sx={{ fontWeight: 'bold' }}>{userFantasyTeams.length}/{primaryLeague.maxTeams}</Typography>
                </Box>
              </Box>
              
              <Button variant="outlined" fullWidth>
                View Standings
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        {primaryTeam && (
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                📊 Team Performance
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography color="text.secondary">Team:</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography sx={{ fontWeight: 'bold', color: 'primary.main' }}>{primaryTeam.name}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography color="text.secondary">Players:</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography sx={{ fontWeight: 'bold' }}>{primaryTeam.players ? primaryTeam.players.length : 0}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography color="text.secondary">Status:</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                  <Typography sx={{ fontWeight: 'bold', color: 'success.main' }}>Active</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Team Stats Overview - Simplified */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              📊 Team Overview
            </Typography>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                    {userFantasyTeams.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'primary.contrastText' }}>
                    Teams
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'secondary.contrastText' }}>
                    {primaryTeam?.players ? primaryTeam.players.length : 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'secondary.contrastText' }}>
                    Players
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.contrastText' }}>
                    {primaryLeague ? 'Active' : 'None'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'success.contrastText' }}>
                    League
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.contrastText' }}>
                    Ready
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'warning.contrastText' }}>
                    Status
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Top Players */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, height: 'fit-content' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                🏆 Top Performers
              </Typography>
              
              {primaryTeam?.players && primaryTeam.players.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {primaryTeam.players
                    .sort((a, b) => (b.player.fantasyPointsPerGame || 0) - (a.player.fantasyPointsPerGame || 0))
                    .slice(0, 5)
                    .map((rosterPlayer, index) => (
                      <Box key={`${rosterPlayer.fantasyTeamId}-${rosterPlayer.playerId}`} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ 
                          bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'primary.main',
                          width: 32, 
                          height: 32,
                          fontSize: '0.75rem'
                        }}>
                          {index + 1}
                        </Avatar>
                        <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => openPlayerDialog(rosterPlayer.player)}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}>
                            {rosterPlayer.player.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {rosterPlayer.player.position} • {rosterPlayer.player.team}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {(rosterPlayer.player.fantasyPointsPerGame || 0).toFixed(1)} FPPG
                        </Typography>
                      </Box>
                    ))}
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No players on roster yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              ⚡ Quick Actions
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                <Button 
                  variant="contained" 
                  fullWidth 
                  startIcon={<TeamIcon />}
                  onClick={() => {
                    // Navigate to players page
                    window.location.href = '/players';
                  }}
                >
                  View Players
                </Button>
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 4px)' }}>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  startIcon={<DraftIcon />}
                  onClick={() => {
                    // Navigate to draft page
                    window.location.href = '/draft';
                  }}
                >
                  Join Draft
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Memoize the loadHomeData function to prevent recreation on every render
  const loadHomeData = useCallback(async () => {
    if (!currentUsername) return;
    
    try {
      setLoading(true);
      setError(null);

      console.log(`🔄 HomePage: Loading data for user "${currentUsername}"...`);
      
      // Load user by username
      const user = await playerService.getUserByUsername(currentUsername);
      console.log('✅ HomePage: User loaded:', user);
      setCurrentUser(user);

      // Load user's fantasy teams using the original method
      const fantasyTeams = await playerService.getFantasyTeamsByUser(user.id);
      console.log('✅ HomePage: Fantasy teams loaded:', fantasyTeams);
      setUserFantasyTeams(fantasyTeams);

      // Load all players for home page display
      const allPlayersData = await playerService.getAllPlayers();
      console.log('✅ HomePage: All players loaded:', allPlayersData.length);
      setAllPlayers(allPlayersData);

      // Set user players to empty array for now - they'll be loaded from the teams data
      setUserPlayers([]);
      
      // Signal page state: has content (always true for home page if loaded successfully)
      setPageState({
        hasContent: true,
        isLoading: false,
        hasError: false,
      });

    } catch (err) {
      console.error('❌ HomePage: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load home data');
      setPageState({
        hasContent: false,
        isLoading: false,
        hasError: true,
      });
    } finally {
      setLoading(false);
    }
  }, [currentUsername, setPageState]);

  // Load data when component mounts or when user changes
  useEffect(() => {
    if (currentUsername && !isUserSwitching) {
      loadHomeData();
    }
  }, [currentUsername, isUserSwitching, loadHomeData]);

  // Listen for player add/remove events to refresh data
  useEffect(() => {
    const handlePlayerAdded = () => {
      loadHomeData();
    };
    const handlePlayerRemoved = () => {
      loadHomeData();
    };

    window.addEventListener('playerAdded', handlePlayerAdded);
    window.addEventListener('playerRemoved', handlePlayerRemoved);

    return () => {
      window.removeEventListener('playerAdded', handlePlayerAdded);
      window.removeEventListener('playerRemoved', handlePlayerRemoved);
    };
  }, [loadHomeData]);

  // Memoize expensive calculations to prevent recalculation on every render
  const { primaryTeam, primaryLeague } = useMemo(() => {
    const primaryTeam = userFantasyTeams[0];
    const primaryLeague = userFantasyLeagues[0];
    return { primaryTeam, primaryLeague };
  }, [userFantasyTeams, userFantasyLeagues]);

  // Add player to team functionality
  const addPlayerToTeam = useCallback(async (player: Player) => {
    if (!primaryTeam || !currentUser) return;
    
    try {
      console.log(`🔄 Adding player ${player.name} to team ${primaryTeam.name}...`);
      
      // Create the new roster player
      const newRosterPlayer: FantasyTeamPlayer = {
        fantasyTeamId: primaryTeam.id,
        playerId: player.id,
        isActive: true,
        acquiredDate: new Date().toISOString(),
        acquisitionType: 'Free Agent',
        player: player
      };
      
      // Update the local state immediately for responsive UI
      setUserFantasyTeams(prevTeams => 
        prevTeams.map(team => 
          team.id === primaryTeam.id 
            ? { ...team, players: [...(team.players || []), newRosterPlayer] }
            : team
        )
      );
      
      // Show success message
      setSnackbarMessage(`✅ ${player.name} added to ${primaryTeam.name}!`);
      setSnackbarOpen(true);
      
      console.log('✅ Player added successfully to team');
      
      // TODO: In a real app, you would also make an API call here to persist the change
      // await playerService.addPlayerToTeam(primaryTeam.id, player.id);
      
    } catch (error) {
      console.error('❌ Error adding player to team:', error);
      setSnackbarMessage(`❌ Failed to add ${player.name} to team`);
      setSnackbarOpen(true);
    }
  }, [primaryTeam, currentUser]);

  // Remove player from team functionality
  const removePlayerFromTeam = useCallback(async (rosterPlayer: FantasyTeamPlayer) => {
    if (!primaryTeam) return;
    
    try {
      console.log(`🔄 Removing player ${rosterPlayer.player.name} from team ${primaryTeam.name}...`);
      
      // Update the local state immediately for responsive UI
      setUserFantasyTeams(prevTeams => 
        prevTeams.map(team => 
          team.id === primaryTeam.id 
            ? { 
                ...team, 
                players: (team.players || []).filter(p => 
                  p.playerId !== rosterPlayer.playerId
                )
              }
            : team
        )
      );
      
      // Show success message
      setSnackbarMessage(`✅ ${rosterPlayer.player.name} removed from ${primaryTeam.name}!`);
      setSnackbarOpen(true);
      
      console.log('✅ Player removed successfully from team');
      
      // TODO: In a real app, you would also make an API call here to persist the change
      // await playerService.removePlayerFromTeam(primaryTeam.id, rosterPlayer.playerId);
      
    } catch (error) {
      console.error('❌ Error removing player from team:', error);
      setSnackbarMessage(`❌ Failed to remove ${rosterPlayer.player.name} from team`);
      setSnackbarOpen(true);
    }
  }, [primaryTeam]);

  // Update page state for loading/error states
  useEffect(() => {
    if (loading || isUserSwitching) {
      setPageState({ isLoading: true, hasContent: false, hasError: false });
    } else if (error) {
      setPageState({ isLoading: false, hasContent: false, hasError: true });
    }
  }, [loading, error, isUserSwitching, setPageState]);

  // Show loading state while user is switching
  if (isUserSwitching) {
    return (
      <Box sx={{ position: 'relative' }}>
        {/* Keep the component mounted but show loading overlay */}
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Switching to {currentUsername}...
            </Typography>
          </Box>
        </Box>
        
        {/* Render the actual content behind the overlay to keep hooks alive */}
        <Box sx={{ opacity: 0.3 }}>
          {renderHomeContent()}
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // Debug info
  console.log('HomePage render:', {
    currentUser,
    userFantasyTeams: userFantasyTeams.length,
    userFantasyLeagues: userFantasyLeagues.length,
    userPlayers: userPlayers.length,
    primaryTeam,
    primaryLeague
  });

  return (
    <>
      {renderHomeContent()}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HomePage;
